import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Input,
  Switch,
  Chip,
  Tooltip,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
} from '@nextui-org/react';

const TIER_OPTIONS = [
  { key: '', label: '— Sin tier —' },
  { key: 'generic', label: 'Generic' },
  { key: 'premium', label: 'Premium' },
  { key: 'organic', label: 'Organic' },
];

const CURRENCY_OPTIONS = [
  { key: 'USD', label: 'USD — Dólar EE.UU.' },
  { key: 'GBP', label: 'GBP — Libra esterlina' },
  { key: 'EUR', label: 'EUR — Euro' },
  { key: 'NZD', label: 'NZD — Dólar NZ' },
  { key: 'AUD', label: 'AUD — Dólar AU' },
  { key: 'BRL', label: 'BRL — Real brasileño' },
  { key: 'MXN', label: 'MXN — Peso mexicano' },
  { key: 'INR', label: 'INR — Rupia india' },
  { key: 'CLP', label: 'CLP — Peso chileno' },
  { key: 'CNY', label: 'CNY — Yuan chino' },
  { key: 'JPY', label: 'JPY — Yen japonés' },
  { key: 'KRW', label: 'KRW — Won coreano' },
  { key: 'SAR', label: 'SAR — Riyal saudí' },
  { key: 'COP', label: 'COP — Peso colombiano' },
  { key: 'RON', label: 'RON — Leu rumano' },
  { key: 'SEK', label: 'SEK — Corona sueca' },
  { key: 'CHF', label: 'CHF — Franco suizo' },
  { key: 'PEN', label: 'PEN — Sol peruano' },
  { key: 'RUB', label: 'RUB — Rublo ruso' },
];
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  listSkusBySpecies,
  createSku,
  updateSku,
  toggleSkuActive,
  triggerSkuScrape,
  listPendingSkuScrapeIds,
  listLatestScrapeStatusForSkus,
  countPricePointsForSkus,
  type LastScrapeStatus,
  deleteSku,
} from 'common/src/api/retail_skus';
import { getSpeciesById, addSpeciesGroup } from 'common/src/api/species';
import { RetailTargetSku } from 'common/src/utils/types';
import TestScrapeModal, { type ScrapeResult } from './TestScrapeModal';
import SkuScrapeDetailModal from './SkuScrapeDetailModal';
import ManualPriceModal from './ManualPriceModal';
import { LoadingState, useConfirm } from '../../../components/ui';

interface Props {
  speciesId: number;
}

type Draft = Partial<RetailTargetSku> & { isNew?: boolean };

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const uniqueSorted = (values: Array<string | null | undefined>) =>
  Array.from(
    new Set(values.map((v) => v?.trim()).filter((v): v is string => !!v))
  ).sort((a, b) => a.localeCompare(b));

const OUTCOME_LABELS: Record<
  LastScrapeStatus['outcome'],
  { label: string; color: 'success' | 'warning' | 'danger' | 'default'; help: string }
> = {
  priced: {
    label: 'OK',
    color: 'success',
    help: 'Último scrape extrajo precio sin problemas.',
  },
  no_product: {
    label: 'Sin precio',
    color: 'warning',
    help: 'Scrape OK pero Claude no encontró precio (out-of-stock, URL muerta, página rota, especie incorrecta...).',
  },
  scrape_error: {
    label: 'Error',
    color: 'danger',
    help: 'Firecrawl o Claude fallaron en el último intento.',
  },
  pending: {
    label: 'Pendiente',
    color: 'default',
    help: 'Scrape en curso o sin parsear.',
  },
  none: {
    label: '—',
    color: 'default',
    help: 'Sin scrapes registrados.',
  },
};

// Antigüedad del último scrape: "hace X días" con color según frescura.
// 0–3 días verde · 3–7 días amarillo · +7 días rojo.
const scrapeAge = (lastScrapedAt: string | null | undefined) => {
  if (!lastScrapedAt) return null;
  const days = Math.floor(
    (Date.now() - new Date(lastScrapedAt).getTime()) / 86_400_000
  );
  const className =
    days <= 3
      ? 'text-success'
      : days <= 7
      ? 'text-warning-600'
      : 'text-danger';
  const label =
    days <= 0 ? 'hoy' : days === 1 ? 'hace 1 día' : `hace ${days} días`;
  return { days, className, label };
};

type AgeFilter = 'all' | 'fresh' | 'stale' | 'old' | 'never';

const AGE_FILTER_OPTIONS: { key: AgeFilter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'fresh', label: '🟢 0–3 días' },
  { key: 'stale', label: '🟡 4–7 días' },
  { key: 'old', label: '🔴 +7 días' },
  { key: 'never', label: 'Nunca scrapeado' },
];

const matchesAgeFilter = (
  lastScrapedAt: string | null | undefined,
  filter: AgeFilter
) => {
  if (filter === 'all') return true;
  const age = scrapeAge(lastScrapedAt);
  if (filter === 'never') return !age;
  if (!age) return false;
  if (filter === 'fresh') return age.days <= 3;
  if (filter === 'stale') return age.days > 3 && age.days <= 7;
  return age.days > 7; // 'old'
};

// Filter by the outcome of the last scrape (differentiate "Sin precio" vs
// "Pendiente" etc.). 'all' shows everything.
type StatusFilter = 'all' | LastScrapeStatus['outcome'];

const STATUS_FILTER_OPTIONS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'priced', label: 'OK (con precio)' },
  { key: 'no_product', label: 'Sin precio' },
  { key: 'pending', label: 'Pendiente' },
  { key: 'scrape_error', label: 'Error' },
  { key: 'none', label: 'Sin scrapes' },
];

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  GB: 'GBP',
  NL: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  FR: 'EUR',
  NZ: 'NZD',
  AU: 'AUD',
  BR: 'BRL',
  MX: 'MXN',
  IN: 'INR',
  CL: 'CLP',
  CN: 'CNY',
  JP: 'JPY',
  KR: 'KRW',
  SA: 'SAR',
  CO: 'COP',
  RO: 'RON',
  SE: 'SEK',
  CH: 'CHF',
  PE: 'PEN',
  RU: 'RUB',
};

const RetailSkusTab: React.FC<Props> = ({ speciesId }) => {
  const [skus, setSkus] = useState<RetailTargetSku[]>([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null); // -1 means "new row"
  const [draft, setDraft] = useState<Draft>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [scrapingId, setScrapingId] = useState<number | null>(null);
  const [varietyOptions, setVarietyOptions] = useState<string[]>([]);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [savingGroupId, setSavingGroupId] = useState<number | null>(null);
  const [pendingScrapeIds, setPendingScrapeIds] = useState<number[]>([]);
  const [lastScrapeStatus, setLastScrapeStatus] = useState<
    Map<number, LastScrapeStatus>
  >(new Map());
  const [pointCounts, setPointCounts] = useState<Map<number, number>>(
    new Map()
  );
  const [scrapeModal, setScrapeModal] = useState<{
    open: boolean;
    sku: RetailTargetSku | null;
    result: ScrapeResult | null;
  }>({ open: false, sku: null, result: null });
  const [detailModal, setDetailModal] = useState<{
    open: boolean;
    sku: RetailTargetSku | null;
  }>({ open: false, sku: null });
  const [manualModal, setManualModal] = useState<{
    open: boolean;
    sku: RetailTargetSku | null;
  }>({ open: false, sku: null });
  const [ageFilter, setAgeFilter] = useState<AgeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [retailerFilter, setRetailerFilter] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, species] = await Promise.all([
        listSkusBySpecies(speciesId),
        getSpeciesById(speciesId).catch(() => null),
      ]);
      setSkus(data);
      setVarietyOptions(
        uniqueSorted([
          ...(species?.varieties ?? []),
          ...(species?.all_varieties ?? []),
          ...data.map((s) => s.variety),
        ])
      );
      setGroupOptions(species?.groups ?? []);
      try {
        const skuIds = data.map((s) => s.id);
        const [pendingIds, statusMap, counts] = await Promise.all([
          listPendingSkuScrapeIds(skuIds),
          listLatestScrapeStatusForSkus(skuIds),
          countPricePointsForSkus(skuIds),
        ]);
        setPendingScrapeIds(pendingIds);
        setLastScrapeStatus(statusMap);
        setPointCounts(counts);
      } catch (e: unknown) {
        setPendingScrapeIds([]);
        setLastScrapeStatus(new Map());
        setPointCounts(new Map());
        toast.error(`Error cargando estado de scrapes: ${getErrorMessage(e)}`);
      }
    } catch (e: unknown) {
      toast.error(`Error cargando SKUs: ${getErrorMessage(e)}`);
    } finally {
      setLoading(false);
    }
  }, [speciesId]);

  useEffect(() => {
    load();
  }, [load]);

  // ---------- Edit / New row helpers ----------

  const startEdit = (sku: RetailTargetSku) => {
    setEditingId(sku.id);
    setDraft({ ...sku });
  };

  const startNew = () => {
    setEditingId(-1);
    setDraft({
      isNew: true,
      species_id: speciesId,
      retailer: '',
      country: '',
      currency: '',
      tier: 'generic',
      product_name: '',
      variety: null,
      species_group: null,
      url: '',
      market_id: null,
      notes: null,
      active: true,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const handleCountryChange = (country: string) => {
    const upperCountry = country.trim().toUpperCase();
    setDraft({
      ...draft,
      country: upperCountry,
      currency: COUNTRY_TO_CURRENCY[upperCountry] ?? draft.currency ?? '',
    });
  };

  const saveDraft = async () => {
    // Basic validation
    const required = [
      'retailer',
      'country',
      'currency',
      'product_name',
      'url',
    ] as const;
    for (const k of required) {
      if (!draft[k]) {
        toast.error(`Falta el campo "${k}"`);
        return;
      }
    }

    setSavingId(editingId);
    try {
      // Si escribió un grupo que todavía no existe en la especie, lo creamos
      // antes de guardar el SKU. Va por RPC porque species solo la puede
      // escribir un superadmin (ver 20260803120000_species_group_rpcs.sql).
      const group = draft.species_group?.trim() || null;
      if (group && !groupOptions.includes(group)) {
        const updated = await addSpeciesGroup(speciesId, group);
        setGroupOptions(updated);
        toast.success(`Grupo "${group}" creado`);
      }

      if (draft.isNew) {
        await createSku({
          species_id: speciesId,
          retailer: draft.retailer!,
          country: draft.country!,
          currency: draft.currency!,
          market_id: draft.market_id ?? null,
          tier: draft.tier ?? null,
          product_name: draft.product_name!,
          variety: draft.variety?.trim() || null,
          species_group: group,
          url: draft.url!,
          active: draft.active ?? true,
          notes: draft.notes ?? null,
        });
        toast.success('SKU agregado');
      } else if (editingId) {
        await updateSku(editingId, {
          retailer: draft.retailer,
          country: draft.country,
          currency: draft.currency,
          tier: draft.tier ?? null,
          product_name: draft.product_name,
          variety: draft.variety?.trim() || null,
          species_group: group,
          url: draft.url,
          notes: draft.notes ?? null,
        });
        toast.success('SKU actualizado');
      }
      cancelEdit();
      load();
    } catch (e: unknown) {
      toast.error(`Error guardando: ${getErrorMessage(e)}`);
    } finally {
      setSavingId(null);
    }
  };

  // Asignación rápida de grupo desde la tabla, sin abrir el editor.
  const handleGroupChange = async (sku: RetailTargetSku, group: string | null) => {
    if ((sku.species_group ?? null) === group) return;
    setSavingGroupId(sku.id);
    try {
      await updateSku(sku.id, { species_group: group });
      setSkus((prev) =>
        prev.map((s) =>
          s.id === sku.id ? { ...s, species_group: group } : s
        )
      );
    } catch (e: unknown) {
      toast.error(`Error guardando grupo: ${getErrorMessage(e)}`);
    } finally {
      setSavingGroupId(null);
    }
  };

  const handleDeleteSku = async (sku: RetailTargetSku) => {
    if (deletingId !== null) return;

    const label = sku.product_name ?? `SKU #${sku.id}`;
    const count = pointCounts.get(sku.id) ?? 0;
    const historyWarning =
      count > 0
        ? `Este SKU tiene ${count} ${
            count === 1 ? 'registro' : 'registros'
          } de precio que también se eliminarán. `
        : '';
    const confirmed = await confirm({
      title: `¿Eliminar "${label}"?`,
      message: `${historyWarning}Esta acción no se puede deshacer.`,
      isDestructive: true,
    });
    if (!confirmed) return;

    setDeletingId(sku.id);
    try {
      await deleteSku(sku.id);
      toast.success('SKU eliminado');
      if (editingId === sku.id) cancelEdit();
      load();
    } catch (e: unknown) {
      toast.error(`No se pudo eliminar: ${getErrorMessage(e)}`);
    } finally {
      setDeletingId(null);
    }
  };

  const deleteDraft = async () => {
    if (!editingId || editingId === -1 || draft.isNew) return;
    await handleDeleteSku(draft as RetailTargetSku);
  };

  // ---------- Active toggle ----------

  const handleToggleActive = async (sku: RetailTargetSku, active: boolean) => {
    try {
      await toggleSkuActive(sku.id, active);
      setSkus((prev) =>
        prev.map((s) => (s.id === sku.id ? { ...s, active } : s))
      );
      toast.success(active ? 'SKU activado' : 'SKU desactivado');
    } catch (e: unknown) {
      toast.error(`Error: ${getErrorMessage(e)}`);
    }
  };

  // ---------- Test scrape ----------

  const handleTestScrape = async (sku: RetailTargetSku) => {
    setScrapingId(sku.id);
    try {
      // Dry-run: doesn't write to DB. Just previews what the scrape WOULD do.
      const result = await triggerSkuScrape(sku.id, { dryRun: true });
      const r = result?.results?.[0] as ScrapeResult | undefined;
      if (r) {
        setScrapeModal({ open: true, sku, result: r });
      } else {
        toast.error('Sin resultado del scrape');
      }
    } catch (e: unknown) {
      toast.error(`Error: ${getErrorMessage(e)}`);
    } finally {
      setScrapingId(null);
    }
  };

  // ---------- Render ----------

  if (loading) {
    return (
      <LoadingState label='Cargando SKUs...' />
    );
  }

  const editPanel =
    editingId !== null ? (
      <div className='rounded-medium border border-default-200 p-4'>
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <h5 className='font-medium'>
                {draft.isNew ? 'Nuevo SKU' : `Editar SKU #${editingId}`}
              </h5>
            </div>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
              <Input
                size='sm'
                label='Retailer'
                value={draft.retailer ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, retailer: e.target.value })
                }
              />
              <Input
                size='sm'
                label='País (ISO-2)'
                placeholder='US, GB, NL...'
                value={draft.country ?? ''}
                onChange={(e) => handleCountryChange(e.target.value)}
                maxLength={2}
              />
              <Select
                size='sm'
                label='Moneda'
                selectedKeys={
                  draft.currency ? new Set([draft.currency]) : new Set([])
                }
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0];
                  setDraft({ ...draft, currency: k ? String(k) : '' });
                }}
              >
                {CURRENCY_OPTIONS.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </Select>
              <Select
                size='sm'
                label='Tier'
                selectedKeys={
                  draft.tier ? new Set([draft.tier]) : new Set([''])
                }
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0];
                  setDraft({ ...draft, tier: k ? String(k) : null });
                }}
              >
                {TIER_OPTIONS.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </Select>
              {/* Autocomplete y no Select: la especie puede no tener grupos
                  todavía, y quien cura los SKUs necesita poder crear el primero
                  sin salir de acá. Al guardar, si el grupo no existe se crea
                  vía RPC (species solo la escribe un superadmin). */}
              <Autocomplete
                size='sm'
                label='Grupo'
                placeholder='Sin grupo'
                allowsCustomValue
                inputValue={draft.species_group ?? ''}
                selectedKey={
                  draft.species_group &&
                  groupOptions.includes(draft.species_group)
                    ? draft.species_group
                    : null
                }
                onInputChange={(value) =>
                  setDraft({ ...draft, species_group: value || null })
                }
                onSelectionChange={(key) => {
                  if (key != null) {
                    setDraft({ ...draft, species_group: String(key) });
                  }
                }}
                description={
                  draft.species_group?.trim() &&
                  !groupOptions.includes(draft.species_group.trim())
                    ? `Se creará el grupo "${draft.species_group.trim()}" en la especie`
                    : undefined
                }
              >
                {groupOptions.map((g) => (
                  <AutocompleteItem key={g} value={g}>
                    {g}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
              <Input
                size='sm'
                label='Nombre del producto'
                className='md:col-span-2'
                value={draft.product_name ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, product_name: e.target.value })
                }
              />
              <Autocomplete
                size='sm'
                label='Variedad'
                placeholder='Ej: Fuji, Granny Smith...'
                allowsCustomValue
                className='md:col-span-2'
                inputValue={draft.variety ?? ''}
                selectedKey={
                  draft.variety && varietyOptions.includes(draft.variety)
                    ? draft.variety
                    : null
                }
                onInputChange={(value) =>
                  // Sin trim() acá: el input es controlado, así que recortar en
                  // cada tecla borra el espacio y hace imposible escribir
                  // variedades de dos palabras ("Granny Smith"). El trim va al
                  // guardar (ver handleSave).
                  setDraft({ ...draft, variety: value || null })
                }
                onSelectionChange={(key) => {
                  if (key != null) {
                    setDraft({ ...draft, variety: String(key) });
                  }
                }}
              >
                {varietyOptions.map((variety) => (
                  <AutocompleteItem key={variety} value={variety}>
                    {variety}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
              <Input
                size='sm'
                label='URL del PDP'
                className='md:col-span-4'
                value={draft.url ?? ''}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                placeholder='https://...'
              />
              <Input
                size='sm'
                label='Notas'
                className='md:col-span-4'
                value={draft.notes ?? ''}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </div>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div>
                {!draft.isNew && editingId !== -1 && (
                  <Button
                    size='sm'
                    color='danger'
                    variant='light'
                    startContent={<Icon icon='tabler:trash' />}
                    onClick={deleteDraft}
                    isLoading={deletingId === editingId}
                    isDisabled={savingId !== null || deletingId !== null}
                  >
                    Eliminar SKU
                  </Button>
                )}
              </div>
              <div className='flex justify-end gap-2'>
                <Button
                  size='sm'
                  variant='light'
                  startContent={<Icon icon='tabler:x' />}
                  onClick={cancelEdit}
                  isDisabled={savingId !== null || deletingId !== null}
                >
                  Cancelar
                </Button>
                <Button
                  size='sm'
                  color='primary'
                  startContent={<Icon icon='tabler:device-floppy' />}
                  onClick={saveDraft}
                  isLoading={savingId !== null}
                  isDisabled={deletingId !== null}
                >
                  Guardar
                </Button>
              </div>
            </div>
          </div>
      </div>
    ) : null;

  const countryOptions = uniqueSorted(skus.map((s) => s.country));
  const retailerOptions = uniqueSorted(skus.map((s) => s.retailer));

  const filteredSkus = skus.filter((sku) => {
    if (!matchesAgeFilter(sku.last_scraped_at, ageFilter)) return false;
    if (countryFilter && sku.country !== countryFilter) return false;
    if (retailerFilter && sku.retailer !== retailerFilter) return false;
    if (statusFilter !== 'all') {
      const outcome = lastScrapeStatus.get(sku.id)?.outcome ?? 'none';
      if (outcome !== statusFilter) return false;
    }
    return true;
  });

  return (
    <div className='space-y-3'>
      <div className='flex items-start justify-between gap-3'>
        <div>
          <h4 className='font-semibold'>SKUs Retail trackeados</h4>
          <p className='text-sm text-default-500'>
            URLs específicas de PDPs que se scrapean cada ciclo. Trackeo por SKU
            fijo garantiza serie de tiempo comparable.
          </p>
        </div>
        <Button
          size='sm'
          color='primary'
          startContent={<Icon icon='tabler:plus' />}
          onClick={startNew}
          isDisabled={editingId !== null}
        >
          Agregar SKU
        </Button>
      </div>

      {/* Filtros de la tabla */}
      <div className='flex flex-wrap items-end gap-2'>
        <Select
          size='sm'
          label='Estado'
          className='w-40'
          selectedKeys={new Set([statusFilter])}
          onSelectionChange={(keys) => {
            const k = Array.from(keys)[0];
            if (k) setStatusFilter(k as StatusFilter);
          }}
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <SelectItem key={o.key} value={o.key}>
              {o.label}
            </SelectItem>
          ))}
        </Select>
        <Select
          size='sm'
          label='Último scrape'
          className='w-40'
          selectedKeys={new Set([ageFilter])}
          onSelectionChange={(keys) => {
            const k = Array.from(keys)[0];
            if (k) setAgeFilter(k as AgeFilter);
          }}
        >
          {AGE_FILTER_OPTIONS.map((o) => (
            <SelectItem key={o.key} value={o.key}>
              {o.label}
            </SelectItem>
          ))}
        </Select>
        <Select
          size='sm'
          label='País'
          className='w-32'
          selectedKeys={new Set([countryFilter || 'all'])}
          onSelectionChange={(keys) => {
            const k = Array.from(keys)[0] as string;
            setCountryFilter(k === 'all' ? '' : k);
          }}
        >
          {[
            <SelectItem key='all' value='all'>
              Todos
            </SelectItem>,
            ...countryOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            )),
          ]}
        </Select>
        <Select
          size='sm'
          label='Retailer'
          className='w-44'
          selectedKeys={new Set([retailerFilter || 'all'])}
          onSelectionChange={(keys) => {
            const k = Array.from(keys)[0] as string;
            setRetailerFilter(k === 'all' ? '' : k);
          }}
        >
          {[
            <SelectItem key='all' value='all'>
              Todos
            </SelectItem>,
            ...retailerOptions.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            )),
          ]}
        </Select>
      </div>

      {editPanel}

      <Table removeWrapper aria-label='Retail SKUs'>
        <TableHeader>
          <TableColumn>Retailer</TableColumn>
          <TableColumn>País</TableColumn>
          <TableColumn>Tier</TableColumn>
                <TableColumn>Grupo</TableColumn>
          <TableColumn>Producto</TableColumn>
          <TableColumn>Variedad</TableColumn>
          <TableColumn>URL</TableColumn>
          <TableColumn>Activo</TableColumn>
          <TableColumn>Último scrape</TableColumn>
          <TableColumn>Registros</TableColumn>
          <TableColumn>Acciones</TableColumn>
        </TableHeader>
        <TableBody emptyContent='No hay SKUs para este filtro'>
          {filteredSkus.map((sku) => {
            const isSkuScraping =
              scrapingId === sku.id || pendingScrapeIds.includes(sku.id);

            return (
              <TableRow key={sku.id}>
                <TableCell>{sku.retailer}</TableCell>
                <TableCell>
                  <Chip size='sm' variant='flat'>
                    {sku.country}
                  </Chip>
                </TableCell>
                <TableCell>
                  {sku.tier ? (
                    <Chip size='sm' variant='flat'>
                      {sku.tier}
                    </Chip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {groupOptions.length === 0 ? (
                    <Tooltip content='Define los grupos de esta especie en Superadmin → Especies'>
                      <span className='text-default-400 text-xs italic cursor-help'>
                        sin grupos
                      </span>
                    </Tooltip>
                  ) : (
                    <Select
                      size='sm'
                      aria-label='Grupo'
                      className='min-w-[130px]'
                      variant='bordered'
                      placeholder='—'
                      isDisabled={savingGroupId === sku.id}
                      selectedKeys={
                        sku.species_group ? new Set([sku.species_group]) : new Set()
                      }
                      onSelectionChange={(keys) => {
                        const k = Array.from(keys)[0];
                        handleGroupChange(sku, k ? String(k) : null);
                      }}
                    >
                      {[
                        { key: '', label: '— Sin grupo —' },
                        ...groupOptions.map((g) => ({ key: g, label: g })),
                      ].map((o) => (
                        <SelectItem key={o.key} value={o.key}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                </TableCell>
                <TableCell className='max-w-[200px] truncate'>
                  <Tooltip content={sku.product_name}>
                    <span>{sku.product_name}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {sku.variety ? (
                    <Chip size='sm' variant='flat'>
                      {sku.variety}
                    </Chip>
                  ) : (
                    <span className='text-default-400'>—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip content={sku.url}>
                    <a
                      href={sku.url}
                      target='_blank'
                      rel='noreferrer'
                      className='text-primary-500 underline text-xs'
                    >
                      Abrir ↗
                    </a>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Switch
                    size='sm'
                    isSelected={sku.active}
                    onValueChange={(v) => handleToggleActive(sku, v)}
                  />
                </TableCell>
                <TableCell className='text-xs text-default-500'>
                  <div className='flex flex-col items-start gap-1'>
                    {isSkuScraping && (
                      <Chip size='sm' color='warning' variant='flat'>
                        Scrapeando
                      </Chip>
                    )}
                    {(() => {
                      const status = lastScrapeStatus.get(sku.id);
                      const outcome = status?.outcome ?? 'none';
                      const cfg = OUTCOME_LABELS[outcome];
                      const tooltip =
                        status?.parse_error?.slice(0, 200) ?? cfg.help;
                      return (
                        <Tooltip content={tooltip}>
                          <Chip
                            size='sm'
                            color={cfg.color}
                            variant='flat'
                            className='cursor-help'
                          >
                            {cfg.label}
                          </Chip>
                        </Tooltip>
                      );
                    })()}
                    {(() => {
                      const age = scrapeAge(sku.last_scraped_at);
                      if (!age)
                        return (
                          <span className='text-default-400'>Nunca</span>
                        );
                      return (
                        <Tooltip
                          content={new Date(
                            sku.last_scraped_at!
                          ).toLocaleString()}
                        >
                          <span
                            className={`font-medium cursor-help ${age.className}`}
                          >
                            {age.label}
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </div>
                </TableCell>
                <TableCell>
                  <Tooltip content='Precios registrados para este SKU'>
                    <Chip size='sm' variant='flat' className='cursor-help'>
                      {pointCounts.get(sku.id) ?? 0}
                    </Chip>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <div className='flex gap-1'>
                    <Tooltip content='Ver historial'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        onClick={() => setDetailModal({ open: true, sku })}
                      >
                        <Icon icon='tabler:eye' />
                      </Button>
                    </Tooltip>
                    <Tooltip content='Editar'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        onClick={() => startEdit(sku)}
                        isDisabled={editingId !== null}
                      >
                        <Icon icon='tabler:edit' />
                      </Button>
                    </Tooltip>
                    <Tooltip content='Test scrape (no guarda punto)'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='success'
                        onClick={() => handleTestScrape(sku)}
                        isLoading={scrapingId === sku.id}
                        isDisabled={
                          scrapingId !== null && scrapingId !== sku.id
                        }
                      >
                        <Icon icon='tabler:player-play' />
                      </Button>
                    </Tooltip>
                    <Tooltip content='Agregar precio manual'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='primary'
                        onClick={() => setManualModal({ open: true, sku })}
                      >
                        <Icon icon='tabler:keyboard' />
                      </Button>
                    </Tooltip>
                    <Tooltip content='Eliminar'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color='danger'
                        onClick={() => handleDeleteSku(sku)}
                        isLoading={deletingId === sku.id}
                        isDisabled={
                          deletingId !== null ||
                          savingId !== null ||
                          scrapingId !== null
                        }
                      >
                        <Icon icon='tabler:trash' />
                      </Button>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TestScrapeModal
        isOpen={scrapeModal.open}
        onClose={() => setScrapeModal({ open: false, sku: null, result: null })}
        sku={scrapeModal.sku}
        result={scrapeModal.result}
      />

      <SkuScrapeDetailModal
        isOpen={detailModal.open}
        onClose={() => setDetailModal({ open: false, sku: null })}
        sku={detailModal.sku}
        isScraping={
          detailModal.sku
            ? scrapingId === detailModal.sku.id ||
              pendingScrapeIds.includes(detailModal.sku.id)
            : false
        }
        onChanged={load}
      />

      <ManualPriceModal
        isOpen={manualModal.open}
        onClose={() => setManualModal({ open: false, sku: null })}
        sku={manualModal.sku}
        onSaved={load}
      />
    </div>
  );
};

export default RetailSkusTab;
